import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { EllipsisVertical } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "../ui/dialog";
import { DialogHeader, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import ScrollArea from "../ui/shared/ScrollArea";
import clsx from "clsx";
import { useContacts } from "@/hooks/useContacts";
import { useAssistants } from "@/hooks/useAssistants";
import type { Database } from "@/lib/supabase";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getDisplayName } from "@/utils/contactUtils";
import { useTeam } from "@/hooks/useTeam";
import { useCustomFields } from "@/hooks/useCustomFields";

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface ContactEditProps {
  contact: AccountContact;
  styles?: string;
}

const ContactEdit: React.FC<ContactEditProps> = ({
  contact,
  styles = "border-[1px] border-[#3F3F464D] bg-neutral-600 w-[34px] h-[34px]",
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { updateContact, deleteContact, refetch, setContacts, setError: setContactsError } = useContacts();
  const { assistants } = useAssistants();
  const { members } = useTeam();
  const { fields: customFields } = useCustomFields();
  const [attributionSearch, setAttributionSearch] = useState('');
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>(
    (contact.custom_fields as Record<string, any>) || {}
  );

  const [formData, setFormData] = useState({
    name: contact.name || '',
    email: contact.email || '',
    phone: contact.phone || '',
    account_assistant_id: contact.account_assistant_id || '',
    assistant_is_active: contact.assistant_is_active || false,
    attribution_id: contact.attribution_id || ''
  });

  const handleFormChange = (field: string, value: any) => {
    // Se for o campo de assistente, ativar automaticamente quando selecionado
    if (field === 'account_assistant_id') {
      const isAssistantSelected = value && value !== 'none' && value !== '';
      setFormData(prev => ({
        ...prev,
        [field]: value,
        assistant_is_active: isAssistantSelected
      }));
      return;
    }

    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Nome do contato é obrigatório' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // Force loading state on contacts list
      setContactsError(null);

      const updateData = {
        name: formData.name,
        display_name: formData.name,
        email: formData.email || null,
        phone: formData.phone || null,
        account_assistant_id: formData.account_assistant_id === 'none' || formData.account_assistant_id === '' ? null : formData.account_assistant_id,
        assistant_is_active: formData.assistant_is_active,
        attribution_id: formData.attribution_id === 'none' || formData.attribution_id === '' ? null : formData.attribution_id,
        custom_fields: customFieldValues
      };

      // Update directly in database to ensure immediate refresh
      if (!supabase || !user) {
        throw new Error('Supabase não está configurado ou usuário não logado');
      }

      const { error: updateError } = await supabase
        .from('account_contacts')
        .update(updateData)
        .eq('id', contact.id)
        .eq('account_id', user.account_id);

      if (updateError) {
        throw new Error(`Erro ao atualizar contato: ${updateError.message}`);
      }
      
      console.log('✅ [ContactEdit] Contato atualizado com sucesso, forçando refresh da listagem...');
      
      setMessage({ type: 'success', text: 'Contato atualizado com sucesso!' });
      
      // Force complete refresh of contacts list
      await refetch();
      
      setTimeout(() => {
        setOpen(false);
      }, 1500);
      
    } catch (error) {
      console.error('❌ [ContactEdit] Erro ao atualizar contato:', error);
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Erro ao atualizar contato' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Tem certeza que deseja excluir este contato?')) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // Force loading state on contacts list
      setContactsError(null);
      
      // Delete directly in database to ensure immediate refresh
      if (!supabase || !user) {
        throw new Error('Supabase não está configurado ou usuário não logado');
      }

      const { error: deleteError } = await supabase
        .from('account_contacts')
        .delete()
        .eq('id', contact.id)
        .eq('account_id', user.account_id);

      if (deleteError) {
        throw new Error(`Erro ao deletar contato: ${deleteError.message}`);
      }
      
      console.log('✅ [ContactEdit] Contato deletado com sucesso, forçando refresh da listagem...');
      
      // Force complete refresh of contacts list
      await refetch();
      setOpen(false);
      
    } catch (error) {
      console.error('❌ [ContactEdit] Erro ao deletar contato:', error);
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Erro ao deletar contato' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = async (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      // Reset form data when opening
      setFormData({
        name: contact.name || getDisplayName(contact),
        email: contact.email || '',
        phone: contact.phone || '',
        account_assistant_id: contact.account_assistant_id || 'none',
        assistant_is_active: contact.assistant_is_active || false,
        attribution_id: contact.attribution_id || 'none'
      });
      setMessage(null);
      setAttributionSearch('');
      setCustomFieldValues((contact.custom_fields as Record<string, any>) || {});
    } else {
      // Refresh contacts list when closing modal
      console.log('🔄 [ContactEdit] Modal fechado, atualizando listagem de contatos...');
      await refetch();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button className={clsx("flex justify-center items-center rounded-[8px]", styles)}>
          <EllipsisVertical size={20} color="#FAFAFA" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="!bg-[#18181B] p-1 w-auto min-w-[140px] flex flex-col gap-1 border-[#3F3F464D] border">
        <button
          onClick={() => {
            setOpen(false);
            navigate(`/crm/${contact.id}`);
          }}
          className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px]"
        >
          Detalhe
        </button>
        <button
          onClick={() => {
            setOpen(false);
            navigate(`/chat/${contact.id}`);
          }}
          className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px]"
        >
          Ir para o chat
        </button>
        <Dialog>
          <DialogTrigger asChild>
            <div className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px]">
              Editar
            </div>
          </DialogTrigger>
          <DialogContent className="flex w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
            <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
              <div className="w-full flex flex-col gap-4">
                {message && (
                  <div className={`p-3 rounded-md ${
                    message.type === 'success' 
                      ? 'bg-green-500/20 border border-green-500/30 text-green-300' 
                      : 'bg-red-500/20 border border-red-500/30 text-red-300'
                  }`}>
                    {message.text}
                  </div>
                )}

                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold text-white">Editar Contato</DialogTitle>
                </DialogHeader>

                {/* Assistente Section */}
                <div className="w-full gap-4 flex flex-col bg-[#2020204D] rounded-[12px] border-[1px] border-[#3F3F464D] p-3">
                  <div className="w-full flex justify-between gap-2">
                    <p className="text-[14px] font-semibold text-neutral-50">
                      Assistente <br /> Ativo
                    </p>
                    <Switch
                      checked={formData.assistant_is_active || false}
                      onCheckedChange={(checked) => {
                        handleFormChange('assistant_is_active', checked);
                      }}
                      activeColor="data-[state=checked]:bg-[#22c55e]"
                      inactiveColor="data-[state=unchecked]:bg-[#3F3F46]"
                      thumbColor="bg-white"
                    />
                  </div>
                  
                  <Select 
                    value={formData.account_assistant_id} 
                    onValueChange={(value) => handleFormChange('account_assistant_id', value)}
                  >
                    <SelectTrigger className="w-full rounded-[12px] bg-[#18181B]">
                      <SelectValue placeholder="Selecione um assistente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="none">Nenhum assistente</SelectItem>
                        {assistants.filter(a => a.is_active).map((assistant) => (
                          <SelectItem key={assistant.id} value={assistant.id}>
                            {assistant.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <form className="w-full flex flex-col gap-4">
                  {/* Nome */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      placeholder="Nome do contato"
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="bg-neutral-600 text-[14px] placeholder:text-[#FAFAFA] text-[#FAFAFA] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                      placeholder="email@exemplo.com"
                      id="email"
                      value={formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#FAFAFA] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="email"
                    />
                  </div>

                  {/* Telefone */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="phone">Telefone</Label>
                    <Input
                      placeholder="(11) 99999-9999"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                      className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#FAFAFA] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="tel"
                    />
                  </div>

                  {/* Responsável pelo Contato */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="attribution">Atribuir Contato Para</Label>
                    <Select
                      value={formData.attribution_id}
                      onValueChange={(value) => handleFormChange('attribution_id', value)}
                      onOpenChange={(open) => {
                        if (!open) setAttributionSearch('');
                      }}
                    >
                      <SelectTrigger className="bg-neutral-600 text-[14px] w-full placeholder:text-[#71717A] text-[#71717A] !h-[40px] border focus:ring-white border-[#3F3F46]">
                        <SelectValue placeholder="Selecione um responsável" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 pb-2">
                          <Input
                            placeholder="Pesquisar responsável..."
                            value={attributionSearch}
                            onChange={(e) => setAttributionSearch(e.target.value)}
                            className="h-8 bg-neutral-700 border-neutral-600 text-neutral-50"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <SelectGroup>
                          <SelectItem value="none">Nenhum responsável</SelectItem>
                          {members
                            .filter(m => m.is_active && m.auth_user_id)
                            .filter(m => {
                              if (!attributionSearch) return true;
                              const searchLower = attributionSearch.toLowerCase();
                              const name = (m.name || '').toLowerCase();
                              const email = (m.email || '').toLowerCase();
                              return name.includes(searchLower) || email.includes(searchLower);
                            })
                            .map((member) => (
                              <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                                {member.name || member.email}
                              </SelectItem>
                            ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-full flex justify-end gap-3 mt-4">
                    <Button 
                      type="button"
                      onClick={handleSave}
                      disabled={loading}
                      className="bg-[#E4E4E7] min-w-[120px]"
                    >
                      {loading ? 'Salvando...' : 'Atualizar'}
                    </Button>
                  </div>
                </form>

                {/* Campos Personalizados */}
                {customFields.filter(f => f.is_active).length > 0 && (
                  <div className="w-full flex flex-col gap-3 border-t border-[#3F3F46] pt-4">
                    <p className="text-sm font-semibold text-neutral-50">Campos Personalizados</p>
                    {customFields.filter(f => f.is_active).map((field) => (
                      <div key={field.id} className="flex flex-col w-full items-start gap-2">
                        <Label htmlFor={`cf-edit-${field.id}`}>
                          {field.name}{field.is_required ? ' *' : ''}
                        </Label>
                        {field.field_type === 'text' && (
                          <Input
                            id={`cf-edit-${field.id}`}
                            placeholder={field.name}
                            value={customFieldValues[field.name] || ''}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                            className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#FAFAFA] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                          />
                        )}
                        {field.field_type === 'number' && (
                          <Input
                            id={`cf-edit-${field.id}`}
                            type="number"
                            placeholder={field.name}
                            value={customFieldValues[field.name] || ''}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                            className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#FAFAFA] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                          />
                        )}
                        {field.field_type === 'date' && (
                          <Input
                            id={`cf-edit-${field.id}`}
                            type="date"
                            value={customFieldValues[field.name] || ''}
                            onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                            className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#FAFAFA] min-h-[36px] border focus:ring-white border-[#3F3F46] [color-scheme:dark]"
                          />
                        )}
                        {(field.field_type === 'select' || field.field_type === 'multi_select') && (
                          <Select
                            value={customFieldValues[field.name] || ''}
                            onValueChange={(value) => setCustomFieldValues(prev => ({ ...prev, [field.name]: value === '__none__' ? '' : value }))}
                          >
                            <SelectTrigger className="bg-neutral-600 text-[14px] w-full placeholder:text-[#71717A] text-[#71717A] !h-[40px] border focus:ring-white border-[#3F3F46]">
                              <SelectValue placeholder={`Selecione ${field.name}`} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem value="__none__">Nenhum</SelectItem>
                                {(field.options || []).map((option: string) => (
                                  <SelectItem key={option} value={option}>{option}</SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Tags Section */}
                <div className="w-full flex flex-col gap-4">
                  <div className="flex gap-3 flex-col">
                    <p className="text-[14px] text-neutral-50 py-4 font-medium border-b-[1px] border-[#3F3F464D]">
                      Tags
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {["Tag1", "Tag2", "Tag3"].map((item, index) => (
                        <Badge key={index}>{item}</Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Campanhas Section */}
                <div className="w-full flex flex-col gap-4">
                  <div className="flex gap-3 flex-col">
                    <p className="text-[14px] text-neutral-50 py-4 font-medium border-b-[1px] border-[#3F3F464D]">
                      Campanhas
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {["Campanha1", "Campanha2", "Campanha3"].map((item, index) => (
                        <Badge key={index}>{item}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
        <button 
          onClick={handleDelete}
          disabled={loading}
          className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px] text-red-400"
        >
          {loading ? 'Removendo...' : 'Remover'}
        </button>
      </PopoverContent>
    </Popover>
  );
};

export default ContactEdit;